// ============ MASTER OF THE MARKET — DATA LAYER ============
// Ported from the design prototype's data.js to a typed ES module.
// In production, the mock arrays below are replaced by the API providers
// described in README.md (see src/services + src/live.ts). The shape of the
// objects is the contract the UI depends on — keep it stable when wiring APIs.

export type AssetClass = 'Stocks' | 'Crypto' | 'Commodities' | 'ETF' | 'Bonds';

export interface Asset {
  sym: string;
  name: string;
  market: string;
  cls: AssetClass;
  price: number;
  chg: number;
  mcap: string;
  pe: number | null;
  pb: number | null;
  roe: number | null;
  divYield: number;
  eps: number | null;
  beta: number;
  sector: string;
  seed: number;
  vol: number;
  score: number;
  /** true once a real provider (not the simulator) has set this price */
  live?: boolean;
}

export interface Index {
  sym: string; name: string; th: string; price: number; chg: number;
  unit: string; seed: number; vol: number; badge: string; c: string;
}

export interface Candle { o: number; h: number; l: number; c: number; v: number; }

// Seeded RNG for deterministic charts
export function mmRng(seed: number): () => number {
  let s = seed >>> 0;
  return function () {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

// Generate OHLCV candles via random walk
export function genCandles(seed: number, base: number, n: number, vol?: number): Candle[] {
  const rnd = mmRng(seed);
  vol = vol || 0.018;
  const out: Candle[] = [];
  let prev = base * (0.78 + rnd() * 0.1);
  for (let i = 0; i < n; i++) {
    const drift = (base - prev) * 0.012;
    const change = (rnd() - 0.48) * vol * prev + drift;
    const open = prev;
    const close = open + change;
    const hi = Math.max(open, close) * (1 + rnd() * vol * 0.6);
    const lo = Math.min(open, close) * (1 - rnd() * vol * 0.6);
    const volm = Math.round((0.6 + rnd()) * 1e6 * (1 + Math.abs(change / prev) * 30));
    out.push({ o: open, h: hi, l: lo, c: close, v: volm });
    prev = close;
  }
  return out;
}

export const ASSETS: Asset[] = [
  // US Stocks
  { sym: 'NVDA', name: 'NVIDIA Corp', market: 'US', cls: 'Stocks', price: 168.42, chg: 2.84, mcap: '4.1T', pe: 46.2, pb: 38.5, roe: 91.3, divYield: 0.03, eps: 3.65, beta: 1.72, sector: 'Semiconductors', seed: 11, vol: 0.028, score: 92 },
  { sym: 'AAPL', name: 'Apple Inc', market: 'US', cls: 'Stocks', price: 214.30, chg: 0.62, mcap: '3.2T', pe: 32.4, pb: 47.1, roe: 147.2, divYield: 0.46, eps: 6.61, beta: 1.21, sector: 'Technology', seed: 12, vol: 0.014, score: 78 },
  { sym: 'MSFT', name: 'Microsoft Corp', market: 'US', cls: 'Stocks', price: 472.18, chg: 1.15, mcap: '3.5T', pe: 36.8, pb: 12.9, roe: 35.6, divYield: 0.68, eps: 12.83, beta: 0.94, sector: 'Software', seed: 13, vol: 0.013, score: 86 },
  { sym: 'TSLA', name: 'Tesla Inc', market: 'US', cls: 'Stocks', price: 286.54, chg: -3.21, mcap: '912B', pe: 118.4, pb: 13.2, roe: 11.8, divYield: 0, eps: 2.42, beta: 2.31, sector: 'Auto / EV', seed: 14, vol: 0.034, score: 61 },
  { sym: 'GOOGL', name: 'Alphabet Inc', market: 'US', cls: 'Stocks', price: 196.77, chg: 0.88, mcap: '2.4T', pe: 24.1, pb: 7.6, roe: 32.1, divYield: 0.41, eps: 8.17, beta: 1.05, sector: 'Internet', seed: 15, vol: 0.015, score: 84 },
  { sym: 'AMZN', name: 'Amazon.com Inc', market: 'US', cls: 'Stocks', price: 228.90, chg: 1.74, mcap: '2.4T', pe: 38.2, pb: 8.9, roe: 24.3, divYield: 0, eps: 5.99, beta: 1.18, sector: 'E-Commerce', seed: 16, vol: 0.018, score: 81 },
  { sym: 'META', name: 'Meta Platforms', market: 'US', cls: 'Stocks', price: 712.45, chg: -0.94, mcap: '1.8T', pe: 28.6, pb: 9.4, roe: 36.9, divYield: 0.29, eps: 24.91, beta: 1.27, sector: 'Social Media', seed: 17, vol: 0.019, score: 80 },
  // Global stocks (local currencies)
  { sym: '7203', name: 'Toyota Motor', market: 'JP', cls: 'Stocks', price: 2980, chg: 0.84, mcap: '¥48T', pe: 9.2, pb: 1.1, roe: 12.4, divYield: 2.84, eps: 324, beta: 0.68, sector: 'Auto', seed: 91, vol: 0.013, score: 79 },
  { sym: '6758', name: 'Sony Group', market: 'JP', cls: 'Stocks', price: 3458, chg: 1.42, mcap: '¥21T', pe: 19.6, pb: 2.6, roe: 13.8, divYield: 0.62, eps: 176, beta: 1.04, sector: 'Electronics', seed: 92, vol: 0.018, score: 77 },
  { sym: '0700', name: 'Tencent Holdings', market: 'HK', cls: 'Stocks', price: 412.6, chg: 2.18, mcap: 'HK$3.8T', pe: 18.4, pb: 4.1, roe: 22.6, divYield: 0.78, eps: 22.4, beta: 1.21, sector: 'Internet', seed: 93, vol: 0.022, score: 81 },
  { sym: '9988', name: 'Alibaba Group', market: 'HK', cls: 'Stocks', price: 118.3, chg: -1.36, mcap: 'HK$2.2T', pe: 13.8, pb: 1.7, roe: 11.2, divYield: 1.34, eps: 8.57, beta: 1.38, sector: 'E-Commerce', seed: 94, vol: 0.026, score: 72 },
  { sym: 'MC', name: 'LVMH', market: 'EU', cls: 'Stocks', price: 685.4, chg: 0.56, mcap: '€342B', pe: 24.8, pb: 5.2, roe: 24.1, divYield: 1.96, eps: 27.6, beta: 1.08, sector: 'Luxury', seed: 95, vol: 0.015, score: 80 },
  { sym: 'ASML', name: 'ASML Holding', market: 'EU', cls: 'Stocks', price: 924.2, chg: 1.88, mcap: '€364B', pe: 38.6, pb: 19.4, roe: 52.3, divYield: 0.92, eps: 23.9, beta: 1.34, sector: 'Semiconductors', seed: 96, vol: 0.021, score: 86 },
  { sym: 'RELI', name: 'Reliance Industries', market: 'IN', cls: 'Stocks', price: 2942, chg: 0.72, mcap: '₹19.9T', pe: 22.4, pb: 2.2, roe: 9.6, divYield: 0.42, eps: 131, beta: 0.94, sector: 'Conglomerate', seed: 97, vol: 0.016, score: 75 },
  { sym: '005930', name: 'Samsung Electronics', market: 'KR', cls: 'Stocks', price: 78600, chg: -0.62, mcap: '₩468T', pe: 12.8, pb: 1.3, roe: 10.8, divYield: 2.12, eps: 6140, beta: 1.12, sector: 'Semiconductors', seed: 98, vol: 0.019, score: 78 },
  // SET (Thai stocks, THB)
  { sym: 'PTT', name: 'ปตท.', market: 'SET', cls: 'Stocks', price: 33.25, chg: 0.76, mcap: '950B฿', pe: 9.8, pb: 0.86, roe: 8.9, divYield: 6.02, eps: 3.39, beta: 0.78, sector: 'Energy', seed: 21, vol: 0.011, score: 72 },
  { sym: 'AOT', name: 'ท่าอากาศยานไทย', market: 'SET', cls: 'Stocks', price: 38.50, chg: -1.28, mcap: '550B฿', pe: 28.4, pb: 4.1, roe: 14.6, divYield: 2.08, eps: 1.36, beta: 1.12, sector: 'Transport', seed: 22, vol: 0.016, score: 64 },
  { sym: 'CPALL', name: 'ซีพี ออลล์', market: 'SET', cls: 'Stocks', price: 52.75, chg: 1.44, mcap: '474B฿', pe: 17.2, pb: 4.2, roe: 25.7, divYield: 2.85, eps: 3.07, beta: 0.89, sector: 'Retail', seed: 23, vol: 0.013, score: 76 },
  { sym: 'DELTA', name: 'เดลต้า อีเลคโทรนิคส์', market: 'SET', cls: 'Stocks', price: 142.00, chg: 3.65, mcap: '1.77T฿', pe: 62.3, pb: 18.4, roe: 31.2, divYield: 0.49, eps: 2.28, beta: 1.65, sector: 'Electronics', seed: 24, vol: 0.027, score: 68 },
  { sym: 'KBANK', name: 'ธนาคารกสิกรไทย', market: 'SET', cls: 'Stocks', price: 158.50, chg: 0.32, mcap: '376B฿', pe: 7.4, pb: 0.68, roe: 9.4, divYield: 5.36, eps: 21.42, beta: 0.92, sector: 'Banking', seed: 25, vol: 0.012, score: 74 },
  // Crypto
  { sym: 'BTC', name: 'Bitcoin', market: 'CRYPTO', cls: 'Crypto', price: 118420, chg: 2.12, mcap: '2.3T', pe: null, pb: null, roe: null, divYield: 0, eps: null, beta: 1.9, sector: 'Layer 1', seed: 31, vol: 0.032, score: 71 },
  { sym: 'ETH', name: 'Ethereum', market: 'CRYPTO', cls: 'Crypto', price: 4286, chg: 3.48, mcap: '516B', pe: null, pb: null, roe: null, divYield: 0, eps: null, beta: 2.2, sector: 'Smart Contract', seed: 32, vol: 0.038, score: 69 },
  { sym: 'SOL', name: 'Solana', market: 'CRYPTO', cls: 'Crypto', price: 212.4, chg: -1.86, mcap: '102B', pe: null, pb: null, roe: null, divYield: 0, eps: null, beta: 2.6, sector: 'Layer 1', seed: 33, vol: 0.045, score: 62 },
  // Commodities
  { sym: 'GOLD', name: 'Gold Spot', market: 'COMEX', cls: 'Commodities', price: 3412.60, chg: 0.54, mcap: '—', pe: null, pb: null, roe: null, divYield: 0, eps: null, beta: 0.12, sector: 'Precious Metal', seed: 41, vol: 0.008, score: 77 },
  { sym: 'WTI', name: 'Crude Oil WTI', market: 'NYMEX', cls: 'Commodities', price: 71.85, chg: -1.12, mcap: '—', pe: null, pb: null, roe: null, divYield: 0, eps: null, beta: 0.85, sector: 'Energy', seed: 42, vol: 0.021, score: 55 },
  // ETF
  { sym: 'SPY', name: 'SPDR S&P 500 ETF', market: 'US', cls: 'ETF', price: 612.84, chg: 0.71, mcap: '628B', pe: 24.8, pb: 4.6, roe: null, divYield: 1.18, eps: null, beta: 1.0, sector: 'Index', seed: 51, vol: 0.009, score: 82 },
  { sym: 'QQQ', name: 'Invesco QQQ Trust', market: 'US', cls: 'ETF', price: 548.12, chg: 1.22, mcap: '342B', pe: 32.1, pb: 8.2, roe: null, divYield: 0.52, eps: null, beta: 1.18, sector: 'Tech Index', seed: 52, vol: 0.012, score: 83 },
  // Bonds
  { sym: 'US10Y', name: 'US Treasury 10Y', market: 'BOND', cls: 'Bonds', price: 4.12, chg: -0.05, mcap: '—', pe: null, pb: null, roe: null, divYield: 4.12, eps: null, beta: -0.3, sector: 'Government', seed: 61, vol: 0.004, score: 66 },
  { sym: 'TGB10Y', name: 'พันธบัตรไทย 10 ปี', market: 'BOND', cls: 'Bonds', price: 2.48, chg: 0.02, mcap: '—', pe: null, pb: null, roe: null, divYield: 2.48, eps: null, beta: -0.2, sector: 'Government', seed: 62, vol: 0.003, score: 63 },
];

// Indices for ticker tape / dashboard hero
export const INDICES: Index[] = [
  { sym: 'SET', name: 'SET Index', th: 'ดัชนี SET', price: 1284.60, chg: 0.42, unit: 'POINT', seed: 71, vol: 0.0035, badge: 'SET', c: '#00b894' },
  { sym: 'MAI', name: 'mai Index', th: 'ตลาดหลักทรัพย์ เอ็ม เอ ไอ', price: 212.94, chg: -0.82, unit: 'POINT', seed: 72, vol: 0.005, badge: 'MAI', c: '#37c8f0' },
  { sym: 'SPX', name: 'S&P 500', th: 'เอสแอนด์พี 500', price: 6112.40, chg: 0.68, unit: 'USD', seed: 73, vol: 0.004, badge: '500', c: '#8b5cf6' },
  { sym: 'NDX', name: 'Nasdaq 100', th: 'แนสแด็ก 100', price: 21480.20, chg: 1.04, unit: 'POINT', seed: 74, vol: 0.006, badge: '100', c: '#5a8df0' },
  { sym: 'DJI', name: 'Dow Jones', th: 'ดาวโจนส์', price: 44128.70, chg: 0.21, unit: 'POINT', seed: 75, vol: 0.003, badge: 'DJI', c: '#f0c75a' },
  { sym: 'NI225', name: 'Japan 225', th: 'นิคเคอิ 225', price: 41260.00, chg: -0.34, unit: 'JPY', seed: 76, vol: 0.005, badge: '225', c: '#ff3b5c' },
  { sym: 'UKX', name: 'FTSE 100', th: 'ฟุตซี่ 100', price: 10254.81, chg: 0.27, unit: 'POINT', seed: 77, vol: 0.003, badge: 'FTS', c: '#5a6acf' },
  { sym: 'GER40', name: 'DAX 40', th: 'เยอรมนี 40', price: 25629.24, chg: 0.07, unit: 'POINT', seed: 84, vol: 0.004, badge: 'GER', c: '#d9a441' },
  { sym: 'FRA40', name: 'CAC 40', th: 'ฝรั่งเศส 40', price: 8509.64, chg: 0.28, unit: 'POINT', seed: 85, vol: 0.004, badge: 'FRA', c: '#6f8ff5' },
  { sym: 'EU50', name: 'Euro Stoxx 50', th: 'ยูโรสต็อกซ์ 50', price: 6358.01, chg: 0.21, unit: 'POINT', seed: 86, vol: 0.004, badge: 'EU', c: '#2f6fed' },
  { sym: 'HK50', name: 'Hang Seng', th: 'ฮั่งเส็ง', price: 25884.43, chg: 0.10, unit: 'POINT', seed: 87, vol: 0.006, badge: 'HK', c: '#e8639b' },
  { sym: 'CN50', name: 'FTSE China A50', th: 'จีน A50', price: 14843.41, chg: 0.38, unit: 'POINT', seed: 88, vol: 0.006, badge: 'CN', c: '#e0554b' },
  { sym: 'AUS200', name: 'ASX 200', th: 'ออสเตรเลีย 200', price: 8976.80, chg: 0.10, unit: 'POINT', seed: 89, vol: 0.004, badge: 'AUS', c: '#00a3a3' },
  { sym: 'USDTHB', name: 'USD/THB', th: 'ดอลลาร์ / บาท', price: 35.84, chg: -0.18, unit: 'THB', seed: 78, vol: 0.002, badge: '฿/$', c: '#7bc86c' },
  // Gold rides in this list so it can headline the dashboard chart. It is also
  // an ASSETS row; both map to GC=F and are filled from the same batched quote.
  { sym: 'GOLD', name: 'Gold Spot', th: 'ทองคำ', price: 3412.60, chg: 0.54, unit: 'USD', seed: 83, vol: 0.008, badge: 'AU', c: '#e0a92e' },
];

// Mini summary cards (dashboard)
export const SUMMARY = [
  { sym: 'TOTAL', th: 'มูลค่าตลาดคริปโต', val: '2.31T', unit: 'USD', chg: 1.86, seed: 81 },
  { sym: 'USDTHB', th: 'USD ต่อ THB', val: '35.84', unit: 'THB', chg: -0.18, seed: 82 },
  { sym: 'XAUUSD', th: 'ทองคำ Spot', val: '3,412.60', unit: 'USD', chg: 0.54, seed: 83 },
  { sym: 'TH10Y', th: 'อัตราผลตอบแทน 10 ปี ไทย', val: '2.48', unit: '%', chg: 0.31, seed: 84 },
];

// Quarterly financials (for fundamental view) keyed by symbol class
export function genFinancials(seed: number, revBase: number) {
  const rnd = mmRng(seed * 7 + 3);
  const out: { y: string; rev: number; ni: number; margin: number }[] = [];
  const years = ['2022', '2023', '2024', '2025', 'TTM'];
  let rev = revBase * 0.62;
  for (let i = 0; i < years.length; i++) {
    rev = rev * (1.04 + rnd() * 0.22);
    const margin = 0.12 + rnd() * 0.26;
    out.push({ y: years[i], rev: rev, ni: rev * margin, margin: margin });
  }
  return out;
}

export const NEWS = [
  { t: 'Fed คงดอกเบี้ยที่ 3.75% ตามคาด ส่งสัญญาณลด 2 ครั้งในครึ่งปีหลัง', src: 'Fed Watch', time: '12 นาที', tag: 'Macro', impact: 'high' },
  { t: 'NVDA เปิดตัวสถาปัตยกรรม Rubin Ultra — นักวิเคราะห์ปรับเป้าขึ้นเป็น $195', src: 'Tech Desk', time: '38 นาที', tag: 'Stocks', impact: 'high' },
  { t: 'SET แตะ 1,285 จุด รับแรงซื้อต่างชาติ 5 วันติด มูลค่ารวม 1.2 หมื่นล้านบาท', src: 'ตลาดหุ้นไทย', time: '1 ชม.', tag: 'SET', impact: 'med' },
  { t: 'Bitcoin ทะลุ $118K อีกครั้ง หลัง ETF ไหลเข้าสุทธิ $850M ในวันเดียว', src: 'Crypto Flow', time: '2 ชม.', tag: 'Crypto', impact: 'med' },
  { t: 'เทคนิค: RSI Divergence คืออะไร? วิธีจับสัญญาณกลับตัวก่อนใคร', src: 'Master Academy', time: '3 ชม.', tag: 'เทคนิค', impact: 'edu' },
  { t: 'OPEC+ คงกำลังการผลิต น้ำมัน WTI ย่อตัวต่ำกว่า $72', src: 'Energy Wire', time: '4 ชม.', tag: 'Commodities', impact: 'med' },
  { t: 'กลยุทธ์ DCA ตาม Human Design Type — Generator ควรลงทุนแบบไหน?', src: 'Master Academy', time: '5 ชม.', tag: 'Human Design', impact: 'edu' },
  { t: 'DELTA ประกาศแผนขยายโรงงาน EV Component รับคำสั่งซื้อปี 2027', src: 'หุ้นไทย Insight', time: '6 ชม.', tag: 'SET', impact: 'med' },
];

// Global IPO calendar — d is ISO yyyy-mm-dd. cur = currency symbol. valNum used for sorting (USD-equiv).
export const IPO_LIST = [
  { sym: 'SPCX', name: 'Space Exploration Technologies (SpaceX)', market: 'NASDAQ', flag: '🇺🇸', d: '2026-06-12', cur: '$', range: '72.00 – 78.00', shares: '555.6M', value: '41.6B', valNum: 41600, status: 'Pricing', heat: 99 },
  { sym: 'IG', name: 'Installatør Gruppen A/S', market: 'OMXCOP', flag: '🇩🇰', d: '2026-06-11', cur: '$', range: '2.33', shares: '70.0M', value: '163.1M', valNum: 163, status: 'Pricing', heat: 62 },
  { sym: '6658', name: 'Liuliumei Co., Ltd. Class H', market: 'HKEX', flag: '🇭🇰', d: '2026-06-15', cur: '$', range: '5.56', shares: '11.46M', value: '438.2M', valNum: 438, status: 'Bookbuild', heat: 70 },
  { sym: 'STARGT', name: 'StarGate Robotics', market: 'NASDAQ', flag: '🇺🇸', d: '2026-06-12', cur: '$', range: '24.00 – 28.00', shares: '42.0M', value: '1.1B', valNum: 1100, status: 'Pricing', heat: 94 },
  { sym: 'NEXAI', name: 'NexaAI Computing', market: 'NYSE', flag: '🇺🇸', d: '2026-06-18', cur: '$', range: '31.00 – 36.00', shares: '58.0M', value: '2.0B', valNum: 2000, status: 'Roadshow', heat: 88 },
  { sym: 'AURA', name: 'Aurora Quantum SE', market: 'XETRA', flag: '🇩🇪', d: '2026-06-16', cur: '€', range: '19.50 – 23.00', shares: '48.0M', value: '1.0B', valNum: 1140, status: 'Roadshow', heat: 79 },
  { sym: 'NSKE', name: 'Nordic Skye Mobility', market: 'OMXSTO', flag: '🇸🇪', d: '2026-06-17', cur: 'kr', range: '95.0 – 110.0', shares: '32.0M', value: '3.3B', valNum: 330, status: 'Bookbuild', heat: 55 },
  { sym: '603xxx', name: 'Hangzhou Lumina Optics', market: 'SSE', flag: '🇨🇳', d: '2026-06-19', cur: '¥', range: '28.60', shares: '120.0M', value: '3.4B', valNum: 478, status: 'Pricing', heat: 84 },
  { sym: 'GREENV', name: 'กรีนเวฟ เอ็นเนอร์ยี', market: 'SET', flag: '🇹🇭', d: '2026-06-24', cur: '฿', range: '4.80 – 5.20', shares: '380.0M', value: '1.9B', valNum: 54, status: 'Bookbuild', heat: 71 },
  { sym: 'TATAEV', name: 'Tata ElectroMobility', market: 'NSE', flag: '🇮🇳', d: '2026-06-22', cur: '₹', range: '410 – 460', shares: '85.0M', value: '39.1B', valNum: 468, status: 'Bookbuild', heat: 86 },
  { sym: 'RKTN', name: 'Rakuten Neo Finance', market: 'TSE', flag: '🇯🇵', d: '2026-06-23', cur: '¥', range: '1,820 – 2,050', shares: '64.0M', value: '131.2B', valNum: 870, status: 'Roadshow', heat: 68 },
  { sym: 'LUMI', name: 'Lumiére Luxury Group', market: 'EURONEXT', flag: '🇫🇷', d: '2026-06-25', cur: '€', range: '42.0 – 48.0', shares: '28.0M', value: '1.3B', valNum: 1480, status: 'Filing', heat: 64 },
  { sym: 'VELA', name: 'Vela Bioscience', market: 'NASDAQ', flag: '🇺🇸', d: '2026-06-26', cur: '$', range: '15.00 – 18.00', shares: '24.0M', value: '432.0M', valNum: 432, status: 'Filing', heat: 59 },
  { sym: 'KSOL', name: 'K-Solar Energy Corp', market: 'KRX', flag: '🇰🇷', d: '2026-06-29', cur: '₩', range: '38,000 – 44,000', shares: '18.0M', value: '792.0B', valNum: 576, status: 'Filing', heat: 61 },
  { sym: 'MEDIX', name: 'เมดิเอ็กซ์ เฮลท์แคร์', market: 'mai', flag: '🇹🇭', d: '2026-07-02', cur: '฿', range: '2.10 – 2.40', shares: '210.0M', value: '480.0M', valNum: 14, status: 'Filing', heat: 58 },
  { sym: 'ORBITX', name: 'OrbitX Space Systems', market: 'NASDAQ', flag: '🇺🇸', d: '2026-07-09', cur: '$', range: '18.00 – 22.00', shares: '36.0M', value: '720.0M', valNum: 720, status: 'Filing', heat: 82 },
  { sym: 'CYBR', name: 'CyberShield Defense', market: 'LSE', flag: '🇬🇧', d: '2026-07-07', cur: '£', range: '3.10 – 3.60', shares: '90.0M', value: '301.5M', valNum: 387, status: 'Filing', heat: 73 },
  { sym: 'ANDES', name: 'Andes Copper Mining', market: 'TSX', flag: '🇨🇦', d: '2026-07-10', cur: 'C$', range: '12.50 – 14.50', shares: '55.0M', value: '742.5M', valNum: 545, status: 'Filing', heat: 66 },
  { sym: 'SAVANA', name: 'Savanna Telecom Group', market: 'JSE', flag: '🇿🇦', d: '2026-07-03', cur: 'R', range: '88.0 – 96.0', shares: '40.0M', value: '3.7B', valNum: 205, status: 'Filing', heat: 52 },
  { sym: 'PETRA', name: 'Petra Renewables', market: 'ASX', flag: '🇦🇺', d: '2026-07-06', cur: 'A$', range: '2.80 – 3.20', shares: '110.0M', value: '330.0M', valNum: 218, status: 'Filing', heat: 57 },
];

// Map ISO weekday IPO counts also need eco/earnings/divs for the week strip — synthesize per day
export function ipoDayCounts(iso: string) {
  const r = mmRng(parseInt(iso.replace(/-/g, '')) % 99991);
  return { eco: Math.floor(r() * 24) + 1, earn: Math.floor(r() * 45) + 2, div: Math.floor(r() * 130) + 5 };
}

export const EARNINGS_LIST = [
  { sym: 'ORCL', name: 'Oracle', date: 'วันนี้ · หลังปิดตลาด', est: '$1.68', last: '$1.42', mcap: '620B', signal: 'beat-likely' },
  { sym: 'ADBE', name: 'Adobe', date: '12 มิ.ย. · หลังปิดตลาด', est: '$5.12', last: '$4.48', mcap: '218B', signal: 'neutral' },
  { sym: 'CPALL', name: 'ซีพี ออลล์', date: '13 มิ.ย. · ก่อนเปิดตลาด', est: '฿0.82', last: '฿0.76', mcap: '474B฿', signal: 'beat-likely' },
  { sym: 'LEN', name: 'Lennar', date: '17 มิ.ย. · หลังปิดตลาด', est: '$2.14', last: '$3.38', mcap: '38B', signal: 'miss-risk' },
  { sym: 'NKE', name: 'Nike', date: '26 มิ.ย. · หลังปิดตลาด', est: '$0.84', last: '$0.99', mcap: '142B', signal: 'neutral' },
  { sym: 'PTT', name: 'ปตท.', date: '8 ส.ค. · ก่อนเปิดตลาด', est: '฿0.94', last: '฿0.88', mcap: '950B฿', signal: 'neutral' },
];

export const DIVIDENDS_LIST = [
  { sym: 'PTT', name: 'ปตท.', co: 'TH', flag: '🇹🇭', exDate: '16 มิ.ย. 2026', payDate: '30 มิ.ย. 2026', amount: '฿1.00', yield: 6.02, freq: 'ครึ่งปี' },
  { sym: 'TISCO', name: 'TISCO Financial', co: 'TH', flag: '🇹🇭', exDate: '17 มิ.ย. 2026', payDate: '1 ก.ค. 2026', amount: '฿8.00', yield: 8.12, freq: 'รายปี' },
  { sym: 'KBANK', name: 'ธ.กสิกรไทย', co: 'TH', flag: '🇹🇭', exDate: '19 มิ.ย. 2026', payDate: '3 ก.ค. 2026', amount: '฿4.25', yield: 5.36, freq: 'ครึ่งปี' },
  { sym: 'AAPL', name: 'Apple', co: 'US', flag: '🇺🇸', exDate: '22 มิ.ย. 2026', payDate: '9 ก.ค. 2026', amount: '$0.26', yield: 0.46, freq: 'รายไตรมาส' },
  { sym: 'MO', name: 'Altria Group', co: 'US', flag: '🇺🇸', exDate: '23 มิ.ย. 2026', payDate: '10 ก.ค. 2026', amount: '$1.02', yield: 7.84, freq: 'รายไตรมาส' },
  { sym: 'SHEL', name: 'Shell plc', co: 'UK', flag: '🇬🇧', exDate: '25 มิ.ย. 2026', payDate: '13 ก.ค. 2026', amount: '£0.34', yield: 3.88, freq: 'รายไตรมาส' },
  { sym: 'HSBA', name: 'HSBC Holdings', co: 'UK', flag: '🇬🇧', exDate: '26 มิ.ย. 2026', payDate: '14 ก.ค. 2026', amount: '£0.53', yield: 7.12, freq: 'ครึ่งปี' },
  { sym: 'MSFT', name: 'Microsoft', co: 'US', flag: '🇺🇸', exDate: '28 มิ.ย. 2026', payDate: '15 ก.ค. 2026', amount: '$0.83', yield: 0.68, freq: 'รายไตรมาส' },
  { sym: 'ALV', name: 'Allianz SE', co: 'EU', flag: '🇪🇺', exDate: '30 มิ.ย. 2026', payDate: '16 ก.ค. 2026', amount: '€13.80', yield: 4.42, freq: 'รายปี' },
  { sym: 'SPY', name: 'SPDR S&P 500', co: 'US', flag: '🇺🇸', exDate: '3 ก.ค. 2026', payDate: '18 ก.ค. 2026', amount: '$1.92', yield: 1.18, freq: 'รายไตรมาส' },
  { sym: '8306', name: 'Mitsubishi UFJ', co: 'JP', flag: '🇯🇵', exDate: '6 ก.ค. 2026', payDate: '22 ก.ค. 2026', amount: '¥28.50', yield: 3.12, freq: 'ครึ่งปี' },
  { sym: '1928', name: 'Sands China', co: 'HK', flag: '🇭🇰', exDate: '9 ก.ค. 2026', payDate: '24 ก.ค. 2026', amount: 'HK$0.58', yield: 6.24, freq: 'ครึ่งปี' },
  { sym: 'CPALL', name: 'ซีพี ออลล์', co: 'TH', flag: '🇹🇭', exDate: '14 ส.ค. 2026', payDate: '28 ส.ค. 2026', amount: '฿0.75', yield: 2.85, freq: 'รายปี' },
];

// ---- Human Design ----
export const HD_TYPES: Record<string, any> = {
  generator: {
    name: 'Generator', th: 'เจเนอเรเตอร์', pop: '37%', aura: 'เปิดและโอบรับ',
    strategy: 'รอการตอบสนอง (Wait to Respond)',
    color: '#00e0b0',
    investor: 'นักลงทุนพลังงานยั่งยืน — เหมาะกับ DCA สม่ำเสมอและถือยาว เมื่อ "ตอบสนอง" กับโอกาสที่ใช่ พลังจะไหลต่อเนื่อง',
    strengths: ['วินัยการลงทุนสม่ำเสมอ', 'พลังถือครองระยะยาว', 'ตอบสนองโอกาสได้แม่นเมื่อฟัง Sacral'],
    cautions: ['อย่าไล่ราคาเพราะความเบื่อ', 'หลีกเลี่ยงการ "ริเริ่ม" เทรดโดยไม่มีสัญญาณ'],
    alloc: { 'หุ้นเติบโต': 30, 'หุ้นปันผล': 25, 'ETF ดัชนี': 25, 'ตราสารหนี้': 12, 'Crypto': 5, 'ทอง': 3 },
    centers: ['sacral', 'throat', 'root', 'splenic'],
  },
  mgen: {
    name: 'Manifesting Generator', th: 'แมนิเฟสติ้ง เจเนอเรเตอร์', pop: '33%', aura: 'เปิดและโอบรับ',
    strategy: 'ตอบสนอง แล้วแจ้งให้ทราบ (Respond, then Inform)',
    color: '#37c8f0',
    investor: 'นักลงทุนหลายเส้นทาง — สลับกลยุทธ์ได้เร็ว เหมาะกับพอร์ตผสม Active + Passive และการ Rebalance บ่อย',
    strengths: ['ปรับตัวเร็วเมื่อตลาดเปลี่ยน', 'จัดการหลายสินทรัพย์พร้อมกัน', 'หาทางลัดที่มีประสิทธิภาพ'],
    cautions: ['ข้ามขั้นตอนวิเคราะห์เพราะใจร้อน', 'เปลี่ยนกลยุทธ์บ่อยจนเสียค่าธรรมเนียม'],
    alloc: { 'หุ้นเติบโต': 35, 'หุ้นปันผล': 15, 'ETF ดัชนี': 20, 'ตราสารหนี้': 10, 'Crypto': 13, 'ทอง': 7 },
    centers: ['sacral', 'throat', 'heart', 'root'],
  },
  projector: {
    name: 'Projector', th: 'โปรเจคเตอร์', pop: '21%', aura: 'โฟกัสและเจาะลึก',
    strategy: 'รอการเชื้อเชิญ (Wait for Invitation)',
    color: '#8b5cf6',
    investor: 'นักวิเคราะห์ผู้มองทะลุ — พลังจำกัดแต่สายตาคม เหมาะกับการศึกษาลึกแล้วลงทุนเป็นรอบ ไม่เฝ้าจอทั้งวัน',
    strengths: ['มองเห็นโครงสร้างที่คนอื่นพลาด', 'วิเคราะห์เชิงลึกได้ดีเยี่ยม', 'จับจังหวะรอบใหญ่ของตลาด'],
    cautions: ['อย่าเทรดถี่จนพลังงานหมด', 'ระวัง FOMO จากการอยากพิสูจน์ตัวเอง'],
    alloc: { 'หุ้นเติบโต': 20, 'หุ้นปันผล': 30, 'ETF ดัชนี': 30, 'ตราสารหนี้': 12, 'Crypto': 4, 'ทอง': 4 },
    centers: ['ajna', 'throat', 'splenic'],
  },
  manifestor: {
    name: 'Manifestor', th: 'แมนิเฟสเตอร์', pop: '8%', aura: 'ปิดและผลักออก',
    strategy: 'แจ้งก่อนลงมือ (Inform before Acting)',
    color: '#ff3b5c',
    investor: 'ผู้ริเริ่มกระแส — กล้าเข้าก่อนตลาด เหมาะกับ Position Trading และธีมการลงทุนใหม่ ๆ ก่อนใคร',
    strengths: ['กล้าตัดสินใจโดยไม่รอใคร', 'จับธีมใหม่ก่อนตลาด', 'แรงผลักดันเริ่มต้นสูง'],
    cautions: ['ขาดความต่อเนื่องในการติดตามพอร์ต', 'ความใจร้อนทำให้ออกเร็วเกินไป'],
    alloc: { 'หุ้นเติบโต': 40, 'หุ้นปันผล': 10, 'ETF ดัชนี': 15, 'ตราสารหนี้': 10, 'Crypto': 18, 'ทอง': 7 },
    centers: ['throat', 'heart', 'root'],
  },
  reflector: {
    name: 'Reflector', th: 'รีเฟลคเตอร์', pop: '1%', aura: 'สะท้อนและสุ่มตัวอย่าง',
    strategy: 'รอรอบจันทรคติ 28 วัน (Wait a Lunar Cycle)',
    color: '#f0c75a',
    investor: 'กระจกสะท้อนตลาด — รับรู้ Sentiment ได้ไวมาก เหมาะกับการตัดสินใจช้า ๆ รายเดือน และพอร์ตเชิงรับ',
    strengths: ['อ่านอารมณ์ตลาดแม่นยำ', 'เป็นกลาง ไม่ติดอคติ', 'เห็นภาพรวมที่คนอื่นมองข้าม'],
    cautions: ['อย่าตัดสินใจใหญ่ในวันเดียว', 'สภาพแวดล้อม (ข่าว/คอมมูนิตี้) มีผลต่อพอร์ตมาก'],
    alloc: { 'หุ้นเติบโต': 10, 'หุ้นปันผล': 25, 'ETF ดัชนี': 35, 'ตราสารหนี้': 20, 'Crypto': 2, 'ทอง': 8 },
    centers: [],
  },
};

// Determine HD type from birthdate (mock deterministic mapping)
export function hdTypeFromBirth(dateStr: string, timeStr?: string): string {
  const d = new Date(dateStr + 'T' + (timeStr || '12:00'));
  if (isNaN(+d)) return 'generator';
  const n = (d.getDate() + d.getMonth() * 3 + d.getFullYear() + (d.getHours() || 0)) % 100;
  if (n < 37) return 'generator';
  if (n < 70) return 'mgen';
  if (n < 91) return 'projector';
  if (n < 99) return 'manifestor';
  return 'reflector';
}

// Technical indicators from real candles. Each figure needs enough bars for
// its own period; with too few it comes back NaN, prints as "—", and its row in
// the matrix votes neutral. Before, a short series still produced numbers: on
// GOOGL's 12MN view (23 yearly bars) MACD printed NaN, "SMA 50" was 23 bars
// divided by 50 (dragging it toward BUY), and "SMA 200" was a 22-bar average
// under that name. Two figures were not indicators at all: the MACD signal line
// was macd x 0.82 and %K was the RSI plus random noise. Both are computed now.
export function computeTech(candles: Candle[]) {
  const closes = candles.map(c => c.c);
  const n = closes.length;
  const last = n ? closes[n - 1] : NaN;
  const ok = (x: number) => Number.isFinite(x);

  // RSI(14), Wilder smoothing across the series.
  let rsi = NaN;
  if (n >= 15) {
    let g = 0, l = 0;
    for (let i = 1; i <= 14; i++) { const d = closes[i] - closes[i - 1]; if (d > 0) g += d; else l -= d; }
    g /= 14; l /= 14;
    for (let i = 15; i < n; i++) {
      const d = closes[i] - closes[i - 1];
      g = (g * 13 + Math.max(d, 0)) / 14;
      l = (l * 13 + Math.max(-d, 0)) / 14;
    }
    rsi = l === 0 ? 100 : 100 - 100 / (1 + g / l);
  }

  const sma = (p: number) => (n >= p ? closes.slice(n - p).reduce((a, b) => a + b, 0) / p : NaN);
  const emaSeries = (src: number[], p: number) => {
    const k = 2 / (p + 1);
    const out: number[] = [];
    let e = src[0];
    out.push(e);
    for (let i = 1; i < src.length; i++) { e = src[i] * k + e * (1 - k); out.push(e); }
    return out;
  };

  // MACD(12,26) and its 9-period signal line.
  let macd = NaN, sig = NaN;
  if (n >= 35) {
    const e12 = emaSeries(closes, 12), e26 = emaSeries(closes, 26);
    const line = closes.map((_, i) => e12[i] - e26[i]).slice(25);
    const s9 = emaSeries(line, 9);
    macd = line[line.length - 1];
    sig = s9[s9.length - 1];
  }

  const sma20 = sma(20), sma50 = sma(50), sma200 = sma(200);

  let bbUpper = NaN, bbLower = NaN;
  if (n >= 20) {
    const sd = Math.sqrt(closes.slice(n - 20).reduce((a, b) => a + (b - sma20) ** 2, 0) / 20);
    bbUpper = sma20 + 2 * sd;
    bbLower = sma20 - 2 * sd;
  }

  // Stochastic %K(14) from highs and lows.
  let stoch = NaN;
  if (n >= 14) {
    const w = candles.slice(n - 14);
    const hi = Math.max(...w.map(c => c.h)), lo = Math.min(...w.map(c => c.l));
    stoch = hi > lo ? ((last - lo) / (hi - lo)) * 100 : 50;
  }

  // ATR(14): mean true range.
  let atr = NaN;
  if (n >= 15) {
    let tr = 0;
    for (let i = n - 14; i < n; i++) {
      const c = candles[i], pc = candles[i - 1].c;
      tr += Math.max(c.h - c.l, Math.abs(c.h - pc), Math.abs(c.l - pc));
    }
    atr = tr / 14;
  }

  const fx = (x: number, d: number) => (ok(x) ? x.toFixed(d) : '—');
  const vs = (level: number) => (!ok(level) || !ok(last) ? 'neutral' : last > level ? 'buy' : 'sell');
  return {
    rsi, macd, macdSignal: sig, macdHist: ok(macd) && ok(sig) ? macd - sig : NaN,
    sma20, sma50, sma200, bbUpper, bbLower, stoch, atr,
    signals: [
      { name: 'RSI (14)', val: fx(rsi, 1), verdict: !ok(rsi) ? 'neutral' : rsi > 70 ? 'sell' : rsi < 30 ? 'buy' : 'neutral' },
      { name: 'MACD (12,26,9)', val: fx(macd, 2), verdict: !ok(macd) || !ok(sig) ? 'neutral' : macd > sig ? 'buy' : 'sell' },
      { name: 'SMA 20', val: fx(sma20, 2), verdict: vs(sma20) },
      { name: 'SMA 50', val: fx(sma50, 2), verdict: vs(sma50) },
      { name: 'SMA 200', val: fx(sma200, 2), verdict: vs(sma200) },
      { name: 'Bollinger Bands', val: ok(bbUpper) ? '±2σ' : '—', verdict: !ok(bbUpper) ? 'neutral' : last > bbUpper ? 'sell' : last < bbLower ? 'buy' : 'neutral' },
      { name: 'Stochastic %K (14)', val: fx(stoch, 1), verdict: !ok(stoch) ? 'neutral' : stoch > 80 ? 'sell' : stoch < 20 ? 'buy' : 'neutral' },
    ],
  };
}

export function fmtNum(x: number | null | undefined, d?: number): string {
  if (x == null) return '—';
  if (d === undefined) d = 2;
  return x.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
}
export function fmtBig(x: number | null | undefined): string {
  if (x == null) return '—';
  if (x >= 1e12) return (x / 1e12).toFixed(2) + 'T';
  if (x >= 1e9) return (x / 1e9).toFixed(2) + 'B';
  if (x >= 1e6) return (x / 1e6).toFixed(1) + 'M';
  if (x >= 1e3) return (x / 1e3).toFixed(1) + 'K';
  return x.toFixed(0);
}
